import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import clsx from "clsx";
import { useEffect, useState, type FC } from "react";
import { weekDays } from "../../consts/consts";
import { useRequest } from "../../contexts/RequestProvider";

import { type RepeatConfig } from "@entities";
import { type WEEK_DAYS } from "@shared/helpers/constants";
import "./RepeatedEvent.scss";

export const RepeatedEvent: FC = () => {
  const {
    repeatConfig: { repeatType },
    setRepeatConfig,
    resetRepeatConfig,
  } = useRequest();
  const [open, setOpen] = useState(false);

  const handleChange = (
    event: SelectChangeEvent<RepeatConfig["repeatType"]>,
  ) => {
    const value = event.target.value as RepeatConfig["repeatType"];
    setRepeatConfig((prev) => ({ ...prev, repeatType: value }));
    if (value === "CUSTOM") setOpen(true);
    if (value === "NO_REPEAT") resetRepeatConfig();
  };

  const mapValueToCustom = (value = "NO_REPEAT") => {
    return value === "WEEKS" || value === "MONTHS" ? "CUSTOM" : value;
  };

  return (
    <Stack className="field-container" alignItems="flex-start">
      <FormLabel className="field-label">
        הגדרת המשימה כחוזרת
      </FormLabel>
      <Select
        className="field"
        sx={{ fontSize: "small", flex: 20 }}
        value={mapValueToCustom(repeatType) as RepeatConfig["repeatType"]}
        onChange={(event) => handleChange(event)}
      >
        <MenuItem sx={{ fontSize: "small" }} value="NO_REPEAT">
          ללא חזרה
        </MenuItem>
        <MenuItem
          onClick={() => setOpen(true)}
          sx={{ fontSize: "small" }}
          value="CUSTOM"
        >
          התאמה אישית
        </MenuItem>
      </Select>
      <RepeatedDialog open={open} onClose={() => setOpen(false)} />
    </Stack>
  );
};

const RepeatedDialog: FC<{ open: boolean; onClose?: () => void; }> = ({
  open,
  onClose = () => { },
}) => {
  const {
    repeatConfig: { days, repeatType, repeatInterval },
    setRepeatConfig,
    setCurrentEvent,
    resetRepeatConfig,
  } = useRequest();
  const [localSelectedDays, setLocalSelectedDays] = useState<(typeof WEEK_DAYS)[number][]>([]);
  const [localSelectedOption, setLocalSelectedOption] =
    useState<RepeatConfig["repeatType"]>("WEEKS");

  useEffect(() => {
    if (open) {
      setLocalSelectedDays(days);
      if (repeatType !== "MONTHS")
        setRepeatConfig((prev) => ({ ...prev, repeatType: "WEEKS" }));
    }
  }, [open, days]);

  const handleSave = () => {
    if (!localSelectedDays.length) {
      resetRepeatConfig();
    } else {
      setRepeatConfig((prev) => ({
        ...prev,
        repeatType: localSelectedOption,
        days: localSelectedDays,
      }));
      setCurrentEvent((prev) => ({ ...prev, isRepeated: true }));
    }
    onClose();
  };

  const handleClose = () => {
    if (!localSelectedDays.length) {
      resetRepeatConfig();
    }
    onClose();
  };

  return (
    <Dialog
      PaperProps={{
        sx: {
          padding: "2%",
          borderRadius: "12px",
          maxWidth: "500px",
          width: "100%",
        },
      }}
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>
        <Typography
          component="span"
          fontSize="1.5rem"
          fontWeight={600}
          color="primary"
          textAlign="center"
        >
          חזרה מותאמת אישית
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack
          className="repeated-event-container"
          gap={3}
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <Stack gap={2} alignItems="center" direction="row" width="100%">
            <Typography fontWeight={500} color="primary">
              חזרה במשך
            </Typography>
            <TextField
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "3rem",
                },
              }}
              value={repeatInterval}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={() =>
                        setRepeatConfig((prev) => ({
                          ...prev,
                          repeatInterval:
                            repeatInterval + 1 <= 20
                              ? repeatInterval + 1
                              : repeatInterval,
                        }))
                      }
                      title="הוסף"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setRepeatConfig((prev) => ({
                          ...prev,
                          repeatInterval:
                            repeatInterval - 1 >= 1
                              ? repeatInterval - 1
                              : repeatInterval,
                        }))
                      }
                      title="הפחת"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              type="number"
            />
            <Select
              sx={{
                flex: 1,
                borderRadius: "8px",
                height: "3rem",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
              value={localSelectedOption}
              onChange={(event) =>
                setLocalSelectedOption(
                  event.target.value as RepeatConfig["repeatType"]
                )
              }
            >
              <MenuItem value="WEEKS">שבועות</MenuItem>
              <MenuItem value="MONTHS">חודשים</MenuItem>
            </Select>
          </Stack>
          <div className="days-container" style={{ width: "100%" }}>
            <Typography
              fontWeight={500}
              color="primary"
              textAlign="center"
              marginBottom="8px"
            >
              בימים
            </Typography>
            <div
              className="days-buttons-container"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {weekDays.map((day: { name: string; value: string; }) => (
                <button
                  type="button"
                  onClick={() => {
                    setLocalSelectedDays((prev) => {
                      if (prev.find((value) => value === day.value))
                        return prev.filter((value) => value !== day.value);
                      else return [...prev, day.value as (typeof WEEK_DAYS)[number]];
                    });
                  }}
                  className={clsx(
                    "day",
                    localSelectedDays.find((value) => value === day.value) &&
                    "chosen"
                  )}
                  key={day.value}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    backgroundColor: localSelectedDays.includes(day.value as (typeof WEEK_DAYS)[number])
                      ? "#1976d2"
                      : "#fff",
                    color: localSelectedDays.includes(day.value as (typeof WEEK_DAYS)[number]) ? "#fff" : "#000",
                    cursor: "pointer",
                    transition: "all 0.3s",
                  }}
                >
                  {day.name}
                </button>
              ))}
            </div>
          </div>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", padding: "16px" }}>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            borderRadius: "8px",
            padding: "8px 16px",
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          שמירה
        </Button>
      </DialogActions>
    </Dialog>
  );
};
